import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, ShieldCheck } from "lucide-react";
import { useClientAuth } from "@/lib/clientAuth";

export default function ClientLogin() {
    const [params] = useSearchParams();
    const tokenFromUrl = params.get("token") || "";
    const navigate = useNavigate();
    const { login, redeem } = useClientAuth();
    const [mode, setMode] = useState(tokenFromUrl ? "redeem" : "login");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [name, setName] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        if (tokenFromUrl) setMode("redeem");
    }, [tokenFromUrl]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            if (mode === "redeem") {
                if (password !== confirm) {
                    setErr("Les deux mots de passe ne coïncident pas.");
                    setBusy(false);
                    return;
                }
                await redeem(tokenFromUrl, password, name);
            } else {
                await login(email.trim().toLowerCase(), password);
            }
            navigate("/client/espace");
        } catch (e) {
            setErr(
                e?.response?.data?.detail ||
                    "Impossible de vous authentifier — vérifiez votre lien ou vos identifiants."
            );
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="client-login"
            className="min-h-screen bg-[#070d18] text-[#f4ecd8] font-sans-modern flex relative overflow-hidden"
        >
            <div
                aria-hidden="true"
                className="absolute inset-0 opacity-[0.05] pointer-events-none"
                style={{
                    backgroundImage:
                        "radial-gradient(circle at 18% 12%, rgba(212,175,55,0.55) 0px, transparent 38%), radial-gradient(circle at 84% 86%, rgba(212,175,55,0.45) 0px, transparent 44%)",
                }}
            />
            <aside className="hidden lg:flex w-[44%] relative border-r border-white/[0.06] p-16 flex-col justify-between">
                <Link to="/" data-testid="client-logo-link" className="flex items-center gap-4 hover:opacity-80 transition">
                    <img src="/brand/icon-gold.png" alt="" className="h-9 w-auto opacity-95" />
                    <div className="flex flex-col leading-none">
                        <span className="font-serif text-[18px] tracking-[0.32em] uppercase" style={{ fontWeight: 400 }}>
                            Espace client
                        </span>
                        <span className="mt-2 text-[10px] tracking-[0.42em] uppercase text-[#d4af37]/85">
                            XV Capital Partners · Côte d'Azur
                        </span>
                    </div>
                </Link>
                <div>
                    <h1 className="font-serif text-[40px] leading-[1.05] mb-8" style={{ fontWeight: 300 }}>
                        Suivi confidentiel{" "}
                        <em className="not-italic text-[#d4af37]">de votre dossier.</em>
                    </h1>
                    <p className="max-w-md text-[14px] leading-[1.9] text-[#f4ecd8]/70 font-light">
                        Accès strictement personnel et nominatif. Les
                        échanges sont chiffrés et chaque connexion fait
                        l'objet d'un enregistrement horodaté.
                    </p>
                    <div className="mt-12 flex items-center gap-3 text-[10px] tracking-[0.32em] uppercase text-[#d4af37]/70">
                        <ShieldCheck strokeWidth={1.2} size={14} />
                        Session 12h · Lien personnel · Audit log
                    </div>
                </div>
                <div className="text-[10px] tracking-[0.32em] uppercase text-[#f4ecd8]/30">
                    XV Capital Partners · Strategic Advisory
                </div>
            </aside>
            <section className="relative flex-1 flex items-center justify-center p-8 lg:p-16">
                <Link
                    to="/"
                    data-testid="client-back-home"
                    className="absolute top-6 right-6 lg:top-8 lg:right-8 inline-flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-[#f4ecd8]/50 hover:text-[#d4af37] transition z-10"
                >
                    <ArrowLeft strokeWidth={1.2} size={14} />
                    Retour à l'accueil
                </Link>
                <div className="w-full max-w-[420px]">
                    <div className="overline text-[#d4af37] mb-6 text-[10px] tracking-[0.36em]">
                        {mode === "redeem" ? "Activation de votre accès" : "Accès client"}
                    </div>
                    <h2 className="font-serif text-[34px] md:text-[40px] leading-[1.05] mb-12" style={{ fontWeight: 300 }}>
                        {mode === "redeem" ? "Bienvenue." : "Reconnexion."}
                    </h2>
                    <form onSubmit={onSubmit} className="space-y-7">
                        {mode === "redeem" ? (
                            <>
                                <div>
                                    <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/80 mb-3">
                                        Votre nom
                                    </label>
                                    <input
                                        data-testid="client-redeem-name"
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-3 text-[15px] font-serif text-[#f4ecd8]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/80 mb-3">
                                        Mot de passe (≥ 8 caractères)
                                    </label>
                                    <input
                                        data-testid="client-redeem-password"
                                        type="password"
                                        required
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-3 text-[15px] font-serif text-[#f4ecd8]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/80 mb-3">
                                        Confirmer le mot de passe
                                    </label>
                                    <input
                                        data-testid="client-redeem-confirm"
                                        type="password"
                                        required
                                        value={confirm}
                                        onChange={(e) => setConfirm(e.target.value)}
                                        className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-3 text-[15px] font-serif text-[#f4ecd8]"
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                <div>
                                    <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/80 mb-3">
                                        Adresse email
                                    </label>
                                    <input
                                        data-testid="client-login-email"
                                        type="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-3 text-[15px] font-serif text-[#f4ecd8]"
                                        autoComplete="username"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/80 mb-3">
                                        Mot de passe
                                    </label>
                                    <input
                                        data-testid="client-login-password"
                                        type="password"
                                        required
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-3 text-[15px] font-serif text-[#f4ecd8]"
                                        autoComplete="current-password"
                                    />
                                </div>
                            </>
                        )}

                        {err && (
                            <div data-testid="client-login-error" className="text-[13px] text-[#e89090] border-l-2 border-[#e89090] pl-4 py-1">
                                {err}
                            </div>
                        )}

                        <button
                            type="submit"
                            data-testid="client-login-submit"
                            disabled={busy}
                            className="group w-full mt-4 inline-flex items-center justify-between px-7 py-4 border border-[#d4af37]/40 text-[#d4af37] text-[11px] tracking-[0.32em] uppercase hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {busy ? "Vérification…" : mode === "redeem" ? "Activer mon accès" : "Accéder à mon espace"}
                            <ArrowRight strokeWidth={1.2} size={14} className="transition-transform duration-500 group-hover:translate-x-1" />
                        </button>
                    </form>
                    <div className="mt-14 text-[10px] tracking-[0.3em] uppercase text-[#f4ecd8]/30 leading-[2]">
                        {mode === "redeem"
                            ? "Premier accès via le lien transmis par le cabinet."
                            : "Vous n'avez pas de compte ? Demandez à votre interlocuteur un lien d'invitation."}
                    </div>
                </div>
            </section>
        </div>
    );
}
